/**
 * Sidebar Component
 *
 * Navigation sidebar for Service Provider Dashboard.
 * PURELY PRESENTATIONAL - displays navigation items and user profile.
 *
 * @example
 * ```tsx
 * <Sidebar
 *   sections={navigationSections}
 *   userProfile={userProfile}
 *   onClose={() => setMobileMenuOpen(false)}
 * />
 * ```
 */

import { NavLink } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import type { NavigationSection, UserProfileViewModel } from './types';

export interface SidebarProps {
  /** Navigation sections to display */
  sections: NavigationSection[];
  /** User profile information */
  userProfile: UserProfileViewModel;
  /** Callback when sidebar should close (mobile) */
  onClose?: () => void;
}

/**
 * Sidebar navigation with sections and user profile.
 * Responsive for mobile and desktop layouts.
 */
export function Sidebar({ sections, userProfile, onClose }: SidebarProps) {
  return (
    <aside className="flex flex-col h-full bg-white border-r border-neutral-200">
      {/* Logo/Brand Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
        <div className="flex items-center">
          <span className="text-xl font-bold text-primary-600">CalWMP</span>
          <span className="ml-2 text-xs font-medium text-neutral-500 bg-neutral-100 px-2 py-1 rounded">
            Provider
          </span>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-neutral-100 lg:hidden focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Close sidebar"
          >
            <svg
              className="w-5 h-5 text-neutral-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Navigation Sections */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {sections.map((section) => (
          <div key={section.id} className="mb-6">
            {section.title && (
              <h3 className="px-3 mb-2 text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                {section.title}
              </h3>
            )}
            <ul className="space-y-1">
              {section.items.map((item) => (
                <li key={item.id}>
                  <NavLink
                    to={item.path}
                    onClick={onClose}
                    className={({ isActive }) =>
                      `flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900'
                      }`
                    }
                  >
                    <div className="flex items-center min-w-0 flex-1">
                      <span className="flex-shrink-0 w-5 h-5 mr-3">
                        {item.icon}
                      </span>
                      <span className="truncate">{item.label}</span>
                    </div>
                    {item.badge && (
                      <Badge
                        variant={
                          item.badge.variant === 'primary'
                            ? 'default'
                            : item.badge.variant === 'danger'
                            ? 'destructive'
                            : 'secondary'
                        }
                        className="ml-2 flex-shrink-0"
                      >
                        {item.badge.count}
                      </Badge>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* User Profile Section */}
      <div className="border-t border-neutral-200 p-4">
        <button
          onClick={userProfile.onProfileClick}
          className="w-full flex items-center space-x-3 p-2 rounded-md hover:bg-neutral-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
          aria-label="User profile"
        >
          {/* Avatar */}
          <div className="flex-shrink-0">
            {userProfile.avatarUrl ? (
              <img
                src={userProfile.avatarUrl}
                alt={userProfile.name}
                className="w-10 h-10 rounded-full"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-sm font-semibold text-primary-700">
                  {userProfile.name
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2)}
                </span>
              </div>
            )}
          </div>

          {/* User Info */}
          <div className="flex-1 min-w-0 text-left">
            <p className="text-sm font-medium text-neutral-900 truncate">
              {userProfile.name}
            </p>
            <p className="text-xs text-neutral-500 truncate">
              {userProfile.company}
            </p>
          </div>

          {/* Dropdown Icon */}
          <svg
            className="flex-shrink-0 w-5 h-5 text-neutral-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 9l4-4 4 4m0 6l-4 4-4-4"
            />
          </svg>
        </button>

        {/* Logout Button */}
        <button
          onClick={userProfile.onLogout}
          className="w-full mt-2 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
        >
          Logout
        </button>
      </div>
    </aside>
  );
}
